import {
    DomainRepository,
    type IDomainRepository
} from "hooks";
import type { DomainAction } from "types";
import { ApiState } from "infrastructure/api";
import { RequestQueryMapper } from "util/request-query-mapper";
import type { TabLayoutProps } from "ui/widgets/StatefulTabs";

export type RepositoryState<T extends object = {}> = T & {
    readonly apiState: ApiState;
};

export type RepositoryProps<TState extends RepositoryState> = TabLayoutProps & {
    readonly repository: Repository<TState>;
};

export const enum RepositoryActionType {
    Fetch = 'RepositoryActionType:Fetch',
    Reset = 'RepositoryActionType:Reset'
}

export type RepositoryAction<T> =
    | DomainAction<
        RepositoryActionType.Fetch,
        T
    >
    | DomainAction<
        RepositoryActionType.Reset,
        undefined
    >;

export type SearchQueryParams = string | RequestQueryMapper;

export interface IRepository extends IDomainRepository {
    readonly isRepositoryLoading: boolean;

    readonly isRepositoryError: boolean;

    readonly isRepositoryEmpty: boolean;

    readonly fetchHandler: (searchQueryParams?: string) => Promise<unknown>;

    fetch(searchQueryParams: SearchQueryParams): Promise<void>;

    init(searchQueryParams: RequestQueryMapper): Promise<void>;
}

export abstract class Repository<TState extends RepositoryState> extends DomainRepository<
    TState,
    RepositoryActionType
> implements IRepository {
    public get isRepositoryLoading() {
        return [
            ApiState.Idle,
            ApiState.Pending
        ].includes(this.state.apiState);
    }

    public get isRepositoryError() {
        return Object.is(
            this.state.apiState,
            ApiState.Failed
        );
    }

    public get isRepositoryEmpty() {
        return true;
    }

    public abstract readonly fetchHandler: (searchQueryParams?: string) => Promise<unknown>;

    public async fetch(searchQueryParams: SearchQueryParams): Promise<void> {
        this.dispatch({
            type: RepositoryActionType.Fetch,
            payload: {
                apiState: ApiState.Pending
            }
        });

        this.dispatch({
            type: RepositoryActionType.Fetch,
            payload: {
                apiState: ApiState.Succeeded,
                data: await this.fetchHandler(searchQueryParams.toString())
            }
        });
    }

    public init(searchQueryParams: RequestQueryMapper): Promise<void> {
        if (!this.shouldSendRequest(this.state.apiState)) {
            return Promise.resolve();
        }

        return this.fetch(searchQueryParams);
    }

    public reset(): void {
        this.dispatch({
            type: RepositoryActionType.Reset,
            payload: undefined
        });
    }
}
